import { Send } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { SIGNUP_MAXIMUM_AGE, SIGNUP_MINIMUM_AGE } from "shared/utils/constants";

import { ChatOptionComponentProps } from "../lookup";
import {
  SearchableSelect,
  SelectOption,
} from "~/components/input/searchable-select";
import { SelfAdjustingTextarea } from "~/components/input/self-adjusting-textarea";
import { useT } from "~/i18n/use-t";
import { getSmBreakpoint } from "~/util/env-utils";
import { useWindowDimensions } from "~/util/hooks";

export const YearOfBirth: React.FC<ChatOptionComponentProps> = ({
  sendResponse,
}) => {
  const t = useT();
  const { width } = useWindowDimensions();

  const ref = useRef<HTMLTextAreaElement>(null);
  const [message, setMessage] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const options = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years: SelectOption[] = [];

    for (let age = SIGNUP_MINIMUM_AGE; age <= SIGNUP_MAXIMUM_AGE; age++) {
      const year = (currentYear - age).toString();
      years.push({
        key: year,
        value: year,
      });
    }

    return years;
  }, []);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const handleSubmit = () => {
    setIsSubmitted(true);
    const { hasResponseBeenSent } = sendResponse({
      message,
      selection: [message],
    });
    setMessage("");

    if (!hasResponseBeenSent) {
      setIsSubmitted(false);
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-screen-sm flex-col gap-1.5 sm:mb-4">
      {width <= getSmBreakpoint() ? (
        <div className="flex flex-nowrap items-start gap-2 rounded-t-xl border-x border-t border-neutral-200 bg-base-100 p-2.5 pl-4 sm:shadow-sm">
          <SelfAdjustingTextarea
            text={message}
            setText={setMessage}
            placeholder={t({ tx: "userData.dateOfBirth" })}
            className="max-h-40 w-full resize-none appearance-none break-words bg-transparent leading-relaxed text-mama-default-primary caret-mama-default-primary outline-none"
            onEnterPressed={handleSubmit}
            autoFocus
            inputMode="numeric"
            canUpdateText={(newText) => /^\d*$/.test(newText)}
          />
          <button
            title="submit"
            type="button"
            className="btn-neutral btn-sm btn ml-auto grid h-10 w-12 place-content-center rounded-btn"
            onClick={handleSubmit}
            disabled={!message.length || isSubmitted}
          >
            <Send width={24} />
          </button>
        </div>
      ) : (
        <div className="mx-auto flex w-full max-w-screen-sm items-center justify-between gap-2">
          <SearchableSelect
            options={options}
            placeholder={t({ tx: "userData.dateOfBirth" })}
            searchPlaceholder={t({ tx: "graph.text.search" })}
            title="year-of-birth"
            disabled={isSubmitted}
            onSelect={(option) => setMessage(option.value)}
            top
          />

          <button
            type="button"
            title="next"
            className="btn-neutral btn-sm btn ml-auto grid h-10 w-12 place-content-center rounded-btn"
            onClick={handleSubmit}
            disabled={isSubmitted}
          >
            <Send width={24} />
          </button>
        </div>
      )}
    </div>
  );
};
